import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export type ConfirmTone = "hot" | "warn" | "accent";

const TONES: Record<ConfirmTone, string> = {
  hot: "bg-status-hot-soft text-status-hot font-semibold",
  warn: "bg-status-warn-soft text-status-warn font-semibold",
  accent: "bg-accent-soft text-foreground",
};

interface InlineConfirmProps {
  text: ReactNode;
  tone: ConfirmTone;
  cancelLabel: string;
  confirmLabel: string;
  onCancel: () => void;
  onConfirm: () => void;
  busy?: boolean;
}

/**
 * The drawer's confirm strip (#1559): a question and two buttons, cancel first,
 * in place of a modal. Used for deleting a payment, a draft or a template, and
 * for the pending e-transfer asks.
 */
export function InlineConfirm({ text, tone, cancelLabel, confirmLabel, onCancel, onConfirm, busy }: InlineConfirmProps) {
  return (
    <div
      role="group"
      className={cn("flex flex-wrap items-center gap-2 rounded-lg px-2.5 py-2 text-[13.5px]", TONES[tone])}
    >
      <span className="min-w-0">{text}</span>
      <span className="flex-1" />
      <span className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" size="sm" className="h-8 bg-card" onClick={onCancel}>
          {cancelLabel}
        </Button>
        {/* A warn ask confirms going ahead (primary); the others confirm a removal (danger). */}
        <Button
          type="button"
          variant={tone === "warn" ? "default" : "outline"}
          size="sm"
          className={cn("h-8", tone !== "warn" && "bg-card text-status-hot border-status-hot-soft hover:bg-card hover:text-status-hot")}
          onClick={onConfirm}
          disabled={busy}
        >
          {confirmLabel}
        </Button>
      </span>
    </div>
  );
}
